import { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { Select, SelectOptionType, SelectRef, useToast } from "@aidkitorg/component-library";
import { usePost } from "../../API";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { ConversationReviewContext } from "./ConversationReviewPage";

type InlineTagsProps = {
  conversationId: string,
  conversationItemId: string;
  createdAt?: number,
  tags: string[];
  onTagsUpdated?: (tags: string[]) => void;
  label?: string;
};

export function InlineTags({ conversationId, conversationItemId, createdAt, tags: initialTags, onTagsUpdated, label = "Tags" }: InlineTagsProps) {
  const updateTags = usePost('/coach/conversation/tags/update');
  const getAllTags = usePost('/coach/conversation/tags/all');

  const [busy, setBusy] = useState<boolean>(false);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [availableTags, setAvailableTags] = useState<SelectOptionType[]>([]);
  const [comboboxKey, setComboboxKey] = useState(0); // Key to force remount
  const ref = useRef<SelectRef>(null);
  const { readOnlyMode } = useContext(ConversationReviewContext);
  const userChanged = useRef<boolean>(false);
  const { toast } = useToast();

  // add new tags if they come in
  useEffect(() => {
    setTags(prev => Array.from(new Set([...prev, ...initialTags])));
  }, [initialTags]);
  
  // Load all available tags
  useAsyncEffect(async () => {
    setBusy(true);
    const response = await getAllTags({});
    if (!('error' in response) && response.tags) {
      setAvailableTags(response.tags.map(tag => ({ value: tag, label: tag })));
    }
    setBusy(false);
  }, []);
  
  const selectOptions: SelectOptionType[] = useMemo(() => {
    return availableTags
      .map(opt => ({ label: opt.label.toString(), value: opt.value }));
  }, [availableTags]);

  const filterOutSelectedTags = useCallback((option: SelectOptionType, query: string) => {
    if (tags.includes(option.value)) return false;
    const labelStr = typeof option.label === 'string' ? option.label : String(option.label);
    return labelStr.trim().toLowerCase().includes(query.trim().toLowerCase());
  }, [tags]);
  
  useAsyncEffect(async (signal) => {
    if (!userChanged.current) return;
    userChanged.current = false;
    try {
      setBusy(true);
      await updateTags({
        conversationId,
        createdAt,
        conversationItemId,
        tags
      }, signal);
    }
    catch (err:any) {
      toast({
        title: err?.message ?? err,
        variant: "error"
      });
    }
    finally {
      if (!signal.aborted) setBusy(false);
    }
  }, [tags]);

  return (
    <div className="py-2">
      <Select
        disabled={busy || readOnlyMode}
        ref={ref}
        description="Select or type to add a tag"
        key={comboboxKey}
        label={label}
        options={selectOptions}
        value={undefined}
        searchable={true}
        filterFunction={filterOutSelectedTags}
        customAllowed={true}
        customOptionBuilder={(query) => ({ label: query, value: query })}
        onChange={(option) => {
          console.log('[DEBUG] onChange', option);
          if (option) {
            userChanged.current = true;
            setTags([...tags, option.value.trim()]);
            setComboboxKey(prev => prev + 1);
          }
        }}
      />

      {/* Display selected tags as removable badges */}
      {tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
            >
              {tag}
              <button
                onClick={() => {
                  userChanged.current = true;
                  setTags(tags.filter(existingTag => existingTag !== tag));
                }}
                disabled={busy || readOnlyMode}
                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Remove tag"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
